import {
  BlogCategorySingleResponseType,
  IAddBlogCategoriesResponse,
  IBlogCategoriesEdit,
  IBlogCategoriesParams,
  IBlogCategoriesRequest,
  IBlogCategoryParams,
  IBlogCategorySingle,
  IBlogEditParams,
  IBlogResponse,
  IBlogsCategoriesResponse,
  IBlogsParams,
  IBlogsResponse,
  IChangeBlogStatusResponse,
  IDeleteBlogCategoryResponse,
  INetworkConfig,
  IResponse,
  ITopBlogResponse,
  LanguageAlphabet,
  PostType,
  TBlogSingleValues,
} from '@/types'
import { IAddBlogCategory } from '@/types/banner'
import { getURLParams } from '@/utils/urls'
import { Endpoints } from '../endpoints'
import { Instance } from '../instance'

const config: INetworkConfig = {
  baseURL: Endpoints.Base,
}

class BlogsApi extends Instance {
  constructor(config: INetworkConfig) {
    super(config)
  }

  getBlogCategories = (params?: IBlogCategoriesRequest): Promise<IResponse<IBlogsCategoriesResponse>> =>
    this.get(`${Endpoints.PostCategoriesAll}/${PostType.Blogs}`, params)

  getSingleBlogCategory = (id: number, language?: LanguageAlphabet): Promise<IResponse<IBlogCategorySingle>> =>
    this.get(`${Endpoints.PostCategories}/${id}`, language ? { headers: { language } } : undefined)

  addBlogCategory = (params: IAddBlogCategory): Promise<IResponse<IAddBlogCategoriesResponse>> =>
    this.post(`${Endpoints.PostCategories}`, params)

  deleteBlogCategory = (params: number): Promise<IResponse<IDeleteBlogCategoryResponse>> =>
    this.delete(`${Endpoints.PostCategories}/${params}`)

  editBlogCategory = (params: IBlogCategoriesEdit): Promise<IResponse<IAddBlogCategoriesResponse>> =>
    this.put(`${Endpoints.PostCategories}/${params.id}`, params)

  getTopBlogs = (): Promise<IResponse<ITopBlogResponse>> => this.get(`${Endpoints.PostTop}/${PostType.Blogs}`)

  getRecommendedBlogs = (): Promise<IResponse<ITopBlogResponse>> =>
    this.get(`${Endpoints.RecommendedBlogs}/${PostType.Blogs}`)

  getBlogs = (params: IBlogsParams): Promise<IResponse<IBlogsResponse>> =>
    this.get(`${Endpoints.AllPosts}/${params.status}/${PostType.Blogs}`, { params })

  unTopBlog = (id: number): Promise<IResponse<IChangeBlogStatusResponse>> =>
    this.patch(`${Endpoints.Posts}/${id}${Endpoints.UnTop}`)

  toTopBlog = (id: number): Promise<IResponse<IChangeBlogStatusResponse>> =>
    this.patch(`${Endpoints.Posts}/${id}${Endpoints.Top}`)

  recommendBlog = (id: number): Promise<IResponse<IChangeBlogStatusResponse>> =>
    this.patch(`${Endpoints.Posts}/${id}${Endpoints.Recommend}`)

  unRecommendBlog = (id: number): Promise<IResponse<IChangeBlogStatusResponse>> =>
    this.patch(`${Endpoints.Posts}/${id}${Endpoints.UnRecommended}`)

  archiveBlog = (id: number): Promise<IResponse<IChangeBlogStatusResponse>> =>
    this.patch(`${Endpoints.Posts}/${id}${Endpoints.Archive}`)

  unArchiveBlog = (id: number): Promise<IResponse<IChangeBlogStatusResponse>> =>
    this.patch(`${Endpoints.Posts}/${id}${Endpoints.UnArchive}`)

  getSingleBlog = (params: string, language?: LanguageAlphabet): Promise<IResponse<IBlogResponse>> =>
    this.get(`${Endpoints.Posts}/${params}`, language ? { headers: { language } } : undefined)

  addBlog = (params: TBlogSingleValues): Promise<IResponse<IBlogResponse>> =>
    this.post(`${Endpoints.Posts}/${params.status}/${PostType.Blogs}`, params.data)

  editBlog = (params: IBlogEditParams): Promise<IResponse<IBlogResponse>> =>
    this.put(`${Endpoints.Posts}/${params.id}/${PostType.Blogs}`, params.data)

  getBlogCategory = (params: IBlogCategoryParams): Promise<IResponse<IBlogCategorySingle>> =>
    this.get(
      getURLParams([Endpoints.PostByCategory, params?.status!, PostType.Blogs], {
        page: '1',
        perPage: '1000',
        categoryId: params.categoryId.toString(),
      }),
    )

  getBlogCategorySingle = (param: number): Promise<IResponse<BlogCategorySingleResponseType>> =>
    this.get(`${Endpoints.PostCategories}/${param}`)
}

export const blogsApi = new BlogsApi(config)
